import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "@/services/apiClient";
import { CalendarDays, DollarSign, Users, Award, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { STATUS_STYLES } from "@/data/mockData";

export default function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
        const response = await apiClient.get(`${apiBaseUrl}/AdminDashboard/dashboard`);
        setDashboard(response.data);
      } catch (requestError) {
        console.error("Failed to load admin dashboard", requestError);
        setError(requestError.response?.data?.message || "Unable to load the admin dashboard.");
      }
    };
    loadDashboard();
  }, []);

  if (!dashboard && !error) return <div className="py-12 text-center text-sm text-slate-500">Loading dashboard...</div>;
  if (error) return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;

  const chart = Array.isArray(dashboard.chart) ? dashboard.chart : [];
  const enrolments = Array.isArray(dashboard.recentEnrolments) ? dashboard.recentEnrolments : [];
  const KPIS = [
    { label: "Today's enrolments", value: Number(dashboard.todayEnrolments || 0).toLocaleString(), icon: CalendarDays, color: "text-orange-600 bg-orange-50" },
    { label: "Revenue (today)", value: `$${Number(dashboard.revenueToday || 0).toLocaleString()}`, icon: DollarSign, color: "text-emerald-600 bg-emerald-50" },
    { label: "Total students", value: Number(dashboard.totalStudents || 0).toLocaleString(), icon: Users, color: "text-blue-600 bg-blue-50" },
    { label: "Completion rate", value: `${Number(dashboard.completionRate || 0).toFixed(1)}%`, icon: Award, color: "text-violet-600 bg-violet-50" },
  ];
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Dashboard</h1>
          <div className="text-sm text-slate-500">Real-time overview · {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {KPIS.map((k) => (
          <div key={k.label} className="wc-card">
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.color}`}><k.icon className="w-5 h-5" /></div>
            </div>
            <div className="text-3xl font-display font-extrabold text-[#0A2540] mt-3">{k.value}</div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="wc-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-semibold text-slate-500">Revenue trend</div>
              <div className="text-2xl font-display font-extrabold text-[#0A2540] mt-1">${Number(dashboard.revenueMonth || 0).toLocaleString()}</div>
            </div>
            <TrendingUp className="w-5 h-5 text-orange-500" />
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Line type="monotone" dataKey="revenue" stroke="#EA580C" strokeWidth={3} dot={{ fill: "#EA580C", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="wc-card">
          <div className="text-sm font-semibold text-slate-500">Students enrolled (6 months)</div>
          <div className="text-2xl font-display font-extrabold text-[#0A2540] mt-1">{chart.reduce((sum, point) => sum + Number(point.students || 0), 0).toLocaleString()}</div>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="students" fill="#0A2540" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="wc-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display font-extrabold text-[#0A2540]">Recent enrolments</div>
            <div className="text-xs text-slate-500">Latest bookings across all locations</div>
          </div>
          <Link to="/admin/enrolments" className="text-xs font-semibold text-orange-600 hover:underline">View all →</Link>
        </div>
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 font-semibold border-b border-slate-200">
              <tr><th className="text-left py-2">ID</th><th className="text-left">Student</th><th className="text-left">Class</th><th className="text-right">Amount</th><th className="text-left">Status</th><th className="text-left">Date</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {enrolments.map((e) => (
                <tr key={e.studentEnrollmentId} className="hover:bg-slate-50">
                  <td className="py-3 font-mono text-xs text-slate-500">E-{e.studentEnrollmentId}</td>
                  <td className="font-semibold">{e.studentName}</td>
                  <td className="text-slate-600">{e.courseName}</td>
                  <td className="text-right font-semibold">${Number(e.amount || 0).toFixed(2)}</td>
                  <td><span className={`wc-badge ${STATUS_STYLES[e.status] || STATUS_STYLES.Pending}`}>{e.status}</span></td>
                  <td className="text-slate-500">{e.date ? new Date(e.date).toLocaleDateString("en-AU") : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
